import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  fetchMonthlyPnL,
  fetchStockSummary,
  fetchStockAgeing,
  fetchLowStockAccessoriesReport,
  fetchTopSellers,
  fetchCustomerBalancesSummary,
  fetchSupplierBalancesSummary,
  fetchRepairsPnLSummary,
} from '@/services/reports';
import { queryKeys } from '@/lib/queryKeys';
import { parseDatabaseError } from '@/lib/errors';
import { ErrorAlert } from '@/components/common/ErrorAlert';
import { CustomerBalanceSummary, SupplierBalanceSummary, RepairPnLSummary } from '@/types/models';
import {
  BarChart3,
  Download,
  Loader2,
  Users,
  Building2,
  Wrench,
  Search,
} from 'lucide-react';

function formatCurrency(val: number | null | undefined): string {
  if (val == null) return '—';
  return `Rs. ${val.toLocaleString('en-PK')}`;
}

type ReportTab =
  | 'pnl'
  | 'stock'
  | 'ageing'
  | 'lowstock'
  | 'topsellers'
  | 'customerBalances'
  | 'supplierBalances'
  | 'repairsPnl';

export const ReportsPage: React.FC = () => {
  const [reportTab, setReportTab] = useState<ReportTab>('pnl');
  const [filterSearch, setFilterSearch] = useState('');

  // Queries
  const { data: pnlList = [], isLoading: loadingPnl, isError: errPnl, error: pnlError } = useQuery({
    queryKey: queryKeys.reports.pnl,
    queryFn: fetchMonthlyPnL,
    enabled: reportTab === 'pnl',
  });

  const { data: stockSummary = [], isLoading: loadingStock } = useQuery({
    queryKey: queryKeys.reports.stockSummary,
    queryFn: fetchStockSummary,
    enabled: reportTab === 'stock',
  });

  const { data: ageingList = [], isLoading: loadingAgeing } = useQuery({
    queryKey: queryKeys.reports.stockAgeing,
    queryFn: fetchStockAgeing,
    enabled: reportTab === 'ageing',
  });

  const { data: lowStockList = [], isLoading: loadingLowStock } = useQuery({
    queryKey: queryKeys.reports.lowStock,
    queryFn: fetchLowStockAccessoriesReport,
    enabled: reportTab === 'lowstock',
  });

  const { data: topSellers = [], isLoading: loadingTop } = useQuery({
    queryKey: ['reports', 'topSellers'],
    queryFn: fetchTopSellers,
    enabled: reportTab === 'topsellers',
  });

  const { data: customerBalances = [], isLoading: loadingCustBal, isError: errCustBal, error: custBalErr } = useQuery<CustomerBalanceSummary[]>({
    queryKey: ['reports', 'customerBalances'],
    queryFn: fetchCustomerBalancesSummary as any,
    enabled: reportTab === 'customerBalances',
  });

  const { data: supplierBalances = [], isLoading: loadingSuppBal, isError: errSuppBal, error: suppBalErr } = useQuery<SupplierBalanceSummary[]>({
    queryKey: ['reports', 'supplierBalances'],
    queryFn: fetchSupplierBalancesSummary as any,
    enabled: reportTab === 'supplierBalances',
  });

  const { data: repairsPnlList = [], isLoading: loadingRepairsPnl, isError: errRepairsPnl, error: repairsPnlErr } = useQuery<RepairPnLSummary[]>({
    queryKey: ['reports', 'repairsPnl'],
    queryFn: fetchRepairsPnLSummary as any,
    enabled: reportTab === 'repairsPnl',
  });

  // Filtered customer and supplier balances
  const filteredCustomerBalances = customerBalances.filter((c) =>
    !filterSearch.trim() ||
    c.name?.toLowerCase().includes(filterSearch.toLowerCase().trim()) ||
    c.phone?.includes(filterSearch.trim()) ||
    c.customer_code?.toLowerCase().includes(filterSearch.toLowerCase().trim())
  );

  const filteredSupplierBalances = supplierBalances.filter((s) =>
    !filterSearch.trim() ||
    s.name?.toLowerCase().includes(filterSearch.toLowerCase().trim()) ||
    s.phone?.includes(filterSearch.trim()) ||
    s.supplier_code?.toLowerCase().includes(filterSearch.toLowerCase().trim())
  );

  // CSV Exporters
  const handleExportPnLCsv = () => {
    if (pnlList.length === 0) return;
    const headers = ['Month', 'Gross Sales', 'Refunds', 'Net Revenue', 'Sold COGS', 'Returned COGS', 'Net COGS', 'Gross Profit', 'Expenses', 'Net Profit'];
    const rows = pnlList.map((p) => [
      p.month,
      p.gross_sales,
      p.refunds,
      p.net_revenue,
      p.sold_cogs,
      p.returned_cogs,
      p.net_cogs,
      p.gross_profit,
      p.expenses,
      p.net_profit,
    ]);
    downloadCsv('pnl_report', headers, rows);
  };

  const handleExportCustomerBalancesCsv = () => {
    if (customerBalances.length === 0) return;
    const headers = ['Customer Code', 'Name', 'Phone', 'Total Invoiced', 'Total Paid', 'Outstanding Balance'];
    const rows = customerBalances.map((c) => [
      c.customer_code || '',
      `"${c.name}"`,
      c.phone || '',
      c.total_invoiced,
      c.total_paid,
      c.outstanding_balance,
    ]);
    downloadCsv('customer_balances', headers, rows);
  };

  const handleExportSupplierBalancesCsv = () => {
    if (supplierBalances.length === 0) return;
    const headers = ['Supplier Code', 'Name', 'Phone', 'Total Purchased', 'Total Paid', 'Outstanding Payable'];
    const rows = supplierBalances.map((s) => [
      s.supplier_code || '',
      `"${s.name}"`,
      s.phone || '',
      s.total_purchased,
      s.total_paid,
      s.outstanding_payable,
    ]);
    downloadCsv('supplier_payables', headers, rows);
  };

  const handleExportRepairsPnlCsv = () => {
    if (repairsPnlList.length === 0) return;
    const headers = ['Month', 'Total Repairs', 'Completed Repairs', 'Labour Revenue', 'Parts Revenue', 'Total Revenue', 'Parts COGS', 'Gross Profit'];
    const rows = repairsPnlList.map((r) => [
      r.month,
      r.total_repairs,
      r.completed_repairs,
      r.total_labour_revenue,
      r.total_parts_revenue,
      r.total_revenue,
      r.total_parts_cogs,
      r.gross_profit,
    ]);
    downloadCsv('repairs_pnl', headers, rows);
  };

  const downloadCsv = (filename: string, headers: string[], rows: any[][]) => {
    const csvContent = 'data:text/csv;charset=utf-8,' + [headers.join(','), ...rows.map((r) => r.join(','))].join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `${filename}_${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="space-y-5 text-xs">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-lg font-bold text-brand-black flex items-center space-x-2">
            <BarChart3 className="w-5 h-5 text-brand-green" />
            <span>Financial Reports & Analytics</span>
          </h1>
          <p className="text-brand-muted mt-0.5">
            Authoritative database views & financial intelligence
          </p>
        </div>

        <div className="flex items-center space-x-2 shrink-0">
          {reportTab === 'pnl' && (
            <button
              onClick={handleExportPnLCsv}
              disabled={pnlList.length === 0}
              className="inline-flex items-center space-x-1.5 px-3 py-2 bg-white text-brand-black font-semibold rounded border border-brand-border hover:bg-brand-bg disabled:opacity-50"
            >
              <Download className="w-4 h-4" />
              <span>Export P&L CSV</span>
            </button>
          )}
          {reportTab === 'customerBalances' && (
            <button
              onClick={handleExportCustomerBalancesCsv}
              disabled={customerBalances.length === 0}
              className="inline-flex items-center space-x-1.5 px-3 py-2 bg-white text-brand-black font-semibold rounded border border-brand-border hover:bg-brand-bg disabled:opacity-50"
            >
              <Download className="w-4 h-4" />
              <span>Export Balances CSV</span>
            </button>
          )}
          {reportTab === 'supplierBalances' && (
            <button
              onClick={handleExportSupplierBalancesCsv}
              disabled={supplierBalances.length === 0}
              className="inline-flex items-center space-x-1.5 px-3 py-2 bg-white text-brand-black font-semibold rounded border border-brand-border hover:bg-brand-bg disabled:opacity-50"
            >
              <Download className="w-4 h-4" />
              <span>Export Payables CSV</span>
            </button>
          )}
          {reportTab === 'repairsPnl' && (
            <button
              onClick={handleExportRepairsPnlCsv}
              disabled={repairsPnlList.length === 0}
              className="inline-flex items-center space-x-1.5 px-3 py-2 bg-white text-brand-black font-semibold rounded border border-brand-border hover:bg-brand-bg disabled:opacity-50"
            >
              <Download className="w-4 h-4" />
              <span>Export Repairs P&L</span>
            </button>
          )}
        </div>
      </div>

      {/* Tabs Switcher */}
      <div className="flex space-x-2 overflow-x-auto pb-1 border-b border-brand-border">
        <button
          onClick={() => { setReportTab('pnl'); setFilterSearch(''); }}
          className={`px-3 py-2 font-bold rounded-t-lg transition-colors border-b-2 whitespace-nowrap ${
            reportTab === 'pnl'
              ? 'border-brand-green text-brand-green bg-green-50/50'
              : 'border-transparent text-brand-muted hover:text-brand-black'
          }`}
        >
          Monthly P&L
        </button>

        <button
          onClick={() => { setReportTab('customerBalances'); setFilterSearch(''); }}
          className={`px-3 py-2 font-bold rounded-t-lg transition-colors border-b-2 whitespace-nowrap ${
            reportTab === 'customerBalances'
              ? 'border-brand-green text-brand-green bg-green-50/50'
              : 'border-transparent text-brand-muted hover:text-brand-black'
          }`}
        >
          Customer Balances
        </button>

        <button
          onClick={() => { setReportTab('supplierBalances'); setFilterSearch(''); }}
          className={`px-3 py-2 font-bold rounded-t-lg transition-colors border-b-2 whitespace-nowrap ${
            reportTab === 'supplierBalances'
              ? 'border-brand-green text-brand-green bg-green-50/50'
              : 'border-transparent text-brand-muted hover:text-brand-black'
          }`}
        >
          Supplier Payables
        </button>

        <button
          onClick={() => { setReportTab('repairsPnl'); setFilterSearch(''); }}
          className={`px-3 py-2 font-bold rounded-t-lg transition-colors border-b-2 whitespace-nowrap ${
            reportTab === 'repairsPnl'
              ? 'border-brand-green text-brand-green bg-green-50/50'
              : 'border-transparent text-brand-muted hover:text-brand-black'
          }`}
        >
          Repairs P&L
        </button>

        <button
          onClick={() => { setReportTab('stock'); setFilterSearch(''); }}
          className={`px-3 py-2 font-bold rounded-t-lg transition-colors border-b-2 whitespace-nowrap ${
            reportTab === 'stock'
              ? 'border-brand-green text-brand-green bg-green-50/50'
              : 'border-transparent text-brand-muted hover:text-brand-black'
          }`}
        >
          Stock Summary
        </button>

        <button
          onClick={() => { setReportTab('ageing'); setFilterSearch(''); }}
          className={`px-3 py-2 font-bold rounded-t-lg transition-colors border-b-2 whitespace-nowrap ${
            reportTab === 'ageing'
              ? 'border-brand-green text-brand-green bg-green-50/50'
              : 'border-transparent text-brand-muted hover:text-brand-black'
          }`}
        >
          Stock Ageing
        </button>

        <button
          onClick={() => { setReportTab('lowstock'); setFilterSearch(''); }}
          className={`px-3 py-2 font-bold rounded-t-lg transition-colors border-b-2 whitespace-nowrap ${
            reportTab === 'lowstock'
              ? 'border-brand-green text-brand-green bg-green-50/50'
              : 'border-transparent text-brand-muted hover:text-brand-black'
          }`}
        >
          Low Stock
        </button>

        <button
          onClick={() => { setReportTab('topsellers'); setFilterSearch(''); }}
          className={`px-3 py-2 font-bold rounded-t-lg transition-colors border-b-2 whitespace-nowrap ${
            reportTab === 'topsellers'
              ? 'border-brand-green text-brand-green bg-green-50/50'
              : 'border-transparent text-brand-muted hover:text-brand-black'
          }`}
        >
          Top Sellers
        </button>
      </div>

      {/* Tab 1: Monthly P&L View */}
      {reportTab === 'pnl' && (
        <div className="space-y-4">
          {errPnl && <ErrorAlert message={parseDatabaseError(pnlError)} />}
          {loadingPnl ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 text-brand-green animate-spin" />
            </div>
          ) : (
            <div className="bg-white border border-brand-border rounded-xl overflow-hidden shadow-xs">
              <div className="p-3 bg-brand-bg border-b flex items-center justify-between font-bold text-brand-black">
                <span>Monthly Profit & Loss Statement (`public.monthly_pnl`)</span>
                <span className="text-[10px] text-brand-muted">Authoritative Revenue, COGS, Expenses & Net Profit</span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-brand-bg border-b border-brand-border text-brand-muted font-semibold uppercase tracking-wide">
                    <tr>
                      <th className="text-left px-4 py-3">Month</th>
                      <th className="text-right px-4 py-3">Gross Sales</th>
                      <th className="text-right px-4 py-3">Cash Refunds</th>
                      <th className="text-right px-4 py-3">Net Revenue</th>
                      <th className="text-right px-4 py-3">Net COGS</th>
                      <th className="text-right px-4 py-3">Gross Profit</th>
                      <th className="text-right px-4 py-3">Expenses</th>
                      <th className="text-right px-4 py-3">Net Profit</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-brand-border">
                    {pnlList.map((p) => (
                      <tr key={p.month} className="hover:bg-brand-bg/60">
                        <td className="px-4 py-3 font-bold text-brand-black">{p.month}</td>
                        <td className="px-4 py-3 text-right">{formatCurrency(p.gross_sales)}</td>
                        <td className="px-4 py-3 text-right text-brand-red">
                          {p.refunds > 0 ? `- ${formatCurrency(p.refunds)}` : '—'}
                        </td>
                        <td className="px-4 py-3 text-right font-bold text-brand-black">
                          {formatCurrency(p.net_revenue)}
                        </td>
                        <td className="px-4 py-3 text-right text-brand-muted">{formatCurrency(p.net_cogs)}</td>
                        <td className="px-4 py-3 text-right font-bold text-blue-700">
                          {formatCurrency(p.gross_profit)}
                        </td>
                        <td className="px-4 py-3 text-right text-red-700">
                          {p.expenses > 0 ? `- ${formatCurrency(p.expenses)}` : '—'}
                        </td>
                        <td className={`px-4 py-3 text-right font-extrabold text-sm ${p.net_profit >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                          {formatCurrency(p.net_profit)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Tab: Customer Account Balances */}
      {reportTab === 'customerBalances' && (
        <div className="space-y-4">
          <div className="relative max-w-sm">
            <input
              type="text"
              placeholder="Filter by customer name or phone..."
              value={filterSearch}
              onChange={(e) => setFilterSearch(e.target.value)}
              className="w-full pl-8 pr-3 py-1.5 border border-brand-border rounded bg-white text-xs"
            />
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-2 text-brand-muted" />
          </div>

          {errCustBal && <ErrorAlert message={parseDatabaseError(custBalErr)} />}
          {loadingCustBal ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 text-brand-green animate-spin" />
            </div>
          ) : (
            <div className="bg-white border border-brand-border rounded-xl overflow-hidden shadow-xs">
              <div className="p-3 bg-brand-bg border-b flex items-center justify-between font-bold text-brand-black">
                <span className="flex items-center space-x-1.5">
                  <Users className="w-4 h-4 text-brand-green" />
                  <span>Customer Account Balances (`public.customer_balances_summary`)</span>
                </span>
                <span className="text-[10px] text-brand-muted">
                  Total Customers: {filteredCustomerBalances.length}
                </span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-brand-bg border-b border-brand-border text-brand-muted font-semibold uppercase tracking-wide">
                    <tr>
                      <th className="text-left px-4 py-3">Customer</th>
                      <th className="text-left px-4 py-3">Phone</th>
                      <th className="text-center px-4 py-3">Sales / Repairs</th>
                      <th className="text-right px-4 py-3">Total Invoiced</th>
                      <th className="text-right px-4 py-3">Total Paid</th>
                      <th className="text-right px-4 py-3">Outstanding Balance</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-brand-border">
                    {filteredCustomerBalances.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="p-6 text-center text-brand-muted italic">
                          No customer balance records found.
                        </td>
                      </tr>
                    ) : (
                      filteredCustomerBalances.map((c) => (
                        <tr key={c.customer_id} className="hover:bg-brand-bg/60">
                          <td className="px-4 py-3 font-semibold text-brand-black">
                            {c.name}
                            {c.customer_code && (
                              <span className="ml-1 text-[10px] text-brand-muted font-mono">[{c.customer_code}]</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-brand-muted">{c.phone || '—'}</td>
                          <td className="px-4 py-3 text-center text-brand-muted">
                            {c.total_sales_count} sales • {c.total_repairs_count} repairs
                          </td>
                          <td className="px-4 py-3 text-right">{formatCurrency(c.total_invoiced)}</td>
                          <td className="px-4 py-3 text-right text-brand-green font-semibold">{formatCurrency(c.total_paid)}</td>
                          <td className={`px-4 py-3 text-right font-bold ${c.outstanding_balance > 0 ? 'text-brand-red' : 'text-brand-green'}`}>
                            {formatCurrency(c.outstanding_balance)}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Tab: Supplier Payables */}
      {reportTab === 'supplierBalances' && (
        <div className="space-y-4">
          <div className="relative max-w-sm">
            <input
              type="text"
              placeholder="Filter by supplier name or phone..."
              value={filterSearch}
              onChange={(e) => setFilterSearch(e.target.value)}
              className="w-full pl-8 pr-3 py-1.5 border border-brand-border rounded bg-white text-xs"
            />
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-2 text-brand-muted" />
          </div>

          {errSuppBal && <ErrorAlert message={parseDatabaseError(suppBalErr)} />}
          {loadingSuppBal ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 text-brand-green animate-spin" />
            </div>
          ) : (
            <div className="bg-white border border-brand-border rounded-xl overflow-hidden shadow-xs">
              <div className="p-3 bg-brand-bg border-b flex items-center justify-between font-bold text-brand-black">
                <span className="flex items-center space-x-1.5">
                  <Building2 className="w-4 h-4 text-brand-green" />
                  <span>Supplier Payables (`public.supplier_balances_summary`)</span>
                </span>
                <span className="text-[10px] text-brand-muted">
                  Total Suppliers: {filteredSupplierBalances.length}
                </span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-brand-bg border-b border-brand-border text-brand-muted font-semibold uppercase tracking-wide">
                    <tr>
                      <th className="text-left px-4 py-3">Supplier</th>
                      <th className="text-left px-4 py-3">Phone</th>
                      <th className="text-center px-4 py-3">Purchases / Returns</th>
                      <th className="text-right px-4 py-3">Total Purchased</th>
                      <th className="text-right px-4 py-3">Total Paid</th>
                      <th className="text-right px-4 py-3">Outstanding Payable</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-brand-border">
                    {filteredSupplierBalances.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="p-6 text-center text-brand-muted italic">
                          No supplier payable records found.
                        </td>
                      </tr>
                    ) : (
                      filteredSupplierBalances.map((s) => (
                        <tr key={s.supplier_id} className="hover:bg-brand-bg/60">
                          <td className="px-4 py-3 font-semibold text-brand-black">
                            {s.name}
                            {s.supplier_code && (
                              <span className="ml-1 text-[10px] text-brand-muted font-mono">[{s.supplier_code}]</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-brand-muted">{s.phone || '—'}</td>
                          <td className="px-4 py-3 text-center text-brand-muted">
                            {s.total_purchases_count} purchases • {s.total_returns_count} returns
                          </td>
                          <td className="px-4 py-3 text-right">{formatCurrency(s.total_purchased)}</td>
                          <td className="px-4 py-3 text-right text-brand-green font-semibold">{formatCurrency(s.total_paid)}</td>
                          <td className={`px-4 py-3 text-right font-bold ${s.outstanding_payable > 0 ? 'text-amber-700' : 'text-brand-green'}`}>
                            {formatCurrency(s.outstanding_payable)}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Tab: Repairs P&L */}
      {reportTab === 'repairsPnl' && (
        <div className="space-y-4">
          {errRepairsPnl && <ErrorAlert message={parseDatabaseError(repairsPnlErr)} />}
          {loadingRepairsPnl ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 text-brand-green animate-spin" />
            </div>
          ) : (
            <div className="bg-white border border-brand-border rounded-xl overflow-hidden shadow-xs">
              <div className="p-3 bg-brand-bg border-b flex items-center justify-between font-bold text-brand-black">
                <span className="flex items-center space-x-1.5">
                  <Wrench className="w-4 h-4 text-brand-green" />
                  <span>Repairs Department P&L (`public.repairs_pnl_summary`)</span>
                </span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-brand-bg border-b border-brand-border text-brand-muted font-semibold uppercase tracking-wide">
                    <tr>
                      <th className="text-left px-4 py-3">Month</th>
                      <th className="text-center px-4 py-3">Jobs (Total / Done)</th>
                      <th className="text-right px-4 py-3">Labour Revenue</th>
                      <th className="text-right px-4 py-3">Parts Revenue</th>
                      <th className="text-right px-4 py-3">Total Revenue</th>
                      <th className="text-right px-4 py-3">Parts Cost</th>
                      <th className="text-right px-4 py-3">Gross Profit</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-brand-border">
                    {repairsPnlList.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="p-6 text-center text-brand-muted italic">
                          No repair financial data available.
                        </td>
                      </tr>
                    ) : (
                      repairsPnlList.map((r) => (
                        <tr key={r.month} className="hover:bg-brand-bg/60">
                          <td className="px-4 py-3 font-bold text-brand-black">{r.month}</td>
                          <td className="px-4 py-3 text-center text-brand-muted font-medium">
                            {r.total_repairs} total • <span className="text-green-700 font-bold">{r.completed_repairs} done</span>
                          </td>
                          <td className="px-4 py-3 text-right">{formatCurrency(r.total_labour_revenue)}</td>
                          <td className="px-4 py-3 text-right">{formatCurrency(r.total_parts_revenue)}</td>
                          <td className="px-4 py-3 text-right font-bold text-brand-black">{formatCurrency(r.total_revenue)}</td>
                          <td className="px-4 py-3 text-right text-brand-muted">{formatCurrency(r.total_parts_cogs)}</td>
                          <td className={`px-4 py-3 text-right font-bold ${r.gross_profit >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                            {formatCurrency(r.gross_profit)}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Tab 2: Stock Summary */}
      {reportTab === 'stock' && (
        <div className="space-y-4">
          {loadingStock ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 text-brand-green animate-spin" />
            </div>
          ) : (
            <div className="bg-white border border-brand-border rounded-xl overflow-hidden shadow-xs">
              <div className="p-3 bg-brand-bg border-b font-bold text-brand-black">
                Inventory Valuation by Status (`public.stock_summary`)
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-brand-bg border-b border-brand-border text-brand-muted font-semibold uppercase tracking-wide">
                    <tr>
                      <th className="text-left px-4 py-3">Status</th>
                      <th className="text-center px-4 py-3">Laptop Count</th>
                      <th className="text-right px-4 py-3">Total Landed Cost</th>
                      <th className="text-right px-4 py-3">Total Retail Value</th>
                      <th className="text-right px-4 py-3">Potential Gross Profit</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-brand-border">
                    {stockSummary.map((s) => (
                      <tr key={s.status} className="hover:bg-brand-bg/60">
                        <td className="px-4 py-3 font-bold text-brand-black uppercase">{s.status.replace('_', ' ')}</td>
                        <td className="px-4 py-3 text-center font-bold">{s.laptop_count}</td>
                        <td className="px-4 py-3 text-right">{formatCurrency(s.cost_value)}</td>
                        <td className="px-4 py-3 text-right font-semibold text-brand-green">{formatCurrency(s.retail_value)}</td>
                        <td className="px-4 py-3 text-right font-bold text-blue-700">{formatCurrency(s.potential_gross_profit)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Tab 3: Stock Ageing Analysis */}
      {reportTab === 'ageing' && (
        <div className="space-y-4">
          {loadingAgeing ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 text-brand-green animate-spin" />
            </div>
          ) : (
            <div className="bg-white border border-brand-border rounded-xl overflow-hidden shadow-xs">
              <div className="p-3 bg-brand-bg border-b font-bold text-brand-black">
                Laptop Inventory Ageing Analysis (`public.stock_ageing`)
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-brand-bg border-b border-brand-border text-brand-muted font-semibold uppercase tracking-wide">
                    <tr>
                      <th className="text-left px-4 py-3">Stock ID</th>
                      <th className="text-left px-4 py-3">Brand / Model</th>
                      <th className="text-center px-4 py-3">Age (Days)</th>
                      <th className="text-center px-4 py-3">Ageing Band</th>
                      <th className="text-right px-4 py-3">Landed Cost</th>
                      <th className="text-right px-4 py-3">Asking Price</th>
                      <th className="text-left px-4 py-3">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-brand-border">
                    {ageingList.map((a) => (
                      <tr key={a.id} className="hover:bg-brand-bg/60">
                        <td className="px-4 py-3 font-mono font-bold text-brand-black">{a.stock_id}</td>
                        <td className="px-4 py-3 font-semibold text-brand-black">{a.brand} {a.model}</td>
                        <td className="px-4 py-3 text-center font-bold">{a.age_days} days</td>
                        <td className="px-4 py-3 text-center">
                          <span
                            className={`px-2 py-0.5 text-[10px] font-bold rounded ${
                              a.ageing_band === '180+ days'
                                ? 'bg-red-100 text-red-800'
                                : a.ageing_band === '91-180 days'
                                ? 'bg-amber-100 text-amber-800'
                                : 'bg-green-100 text-green-800'
                            }`}
                          >
                            {a.ageing_band}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right">{formatCurrency(a.landed_cost)}</td>
                        <td className="px-4 py-3 text-right font-bold text-brand-green">{formatCurrency(a.asking_price)}</td>
                        <td className="px-4 py-3 text-brand-black uppercase font-semibold text-[10px]">{a.status}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Tab 4: Low Stock Accessories */}
      {reportTab === 'lowstock' && (
        <div className="space-y-4">
          {loadingLowStock ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 text-brand-green animate-spin" />
            </div>
          ) : (
            <div className="bg-white border border-brand-border rounded-xl overflow-hidden shadow-xs">
              <div className="p-3 bg-brand-bg border-b font-bold text-brand-black">
                Low Stock Accessories (`public.low_stock_accessories`)
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-brand-bg border-b border-brand-border text-brand-muted font-semibold uppercase tracking-wide">
                    <tr>
                      <th className="text-left px-4 py-3">SKU</th>
                      <th className="text-left px-4 py-3">Item Name</th>
                      <th className="text-left px-4 py-3">Category</th>
                      <th className="text-center px-4 py-3">Current Stock</th>
                      <th className="text-center px-4 py-3">Reorder Threshold</th>
                      <th className="text-right px-4 py-3">Selling Price</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-brand-border">
                    {lowStockList.map((l) => (
                      <tr key={l.id} className="hover:bg-brand-bg/60">
                        <td className="px-4 py-3 font-mono font-bold text-brand-black">{l.sku}</td>
                        <td className="px-4 py-3 font-semibold text-brand-black">{l.name}</td>
                        <td className="px-4 py-3 text-brand-muted">{l.category || 'General'}</td>
                        <td className="px-4 py-3 text-center font-bold text-red-700">{l.quantity}</td>
                        <td className="px-4 py-3 text-center font-semibold text-brand-muted">{l.reorder_level}</td>
                        <td className="px-4 py-3 text-right font-bold text-brand-green">{formatCurrency(l.selling_price)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Tab 5: Top Selling Products */}
      {reportTab === 'topsellers' && (
        <div className="space-y-4">
          {loadingTop ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 text-brand-green animate-spin" />
            </div>
          ) : (
            <div className="bg-white border border-brand-border rounded-xl overflow-hidden shadow-xs">
              <div className="p-3 bg-brand-bg border-b font-bold text-brand-black">
                Top 10 Selling Products & Services
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-brand-bg border-b border-brand-border text-brand-muted font-semibold uppercase tracking-wide">
                    <tr>
                      <th className="text-left px-4 py-3">Product / Description</th>
                      <th className="text-left px-4 py-3">Item Type</th>
                      <th className="text-center px-4 py-3">Units Sold</th>
                      <th className="text-right px-4 py-3">Total Revenue Generated</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-brand-border">
                    {topSellers.map((t, idx) => (
                      <tr key={idx} className="hover:bg-brand-bg/60">
                        <td className="px-4 py-3 font-semibold text-brand-black">{t.itemName}</td>
                        <td className="px-4 py-3 font-mono text-brand-muted uppercase text-[10px]">{t.itemType}</td>
                        <td className="px-4 py-3 text-center font-bold">{t.totalQuantity}</td>
                        <td className="px-4 py-3 text-right font-bold text-brand-green">{formatCurrency(t.totalRevenue)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
